import React from 'react';
import { format } from 'date-fns';
import { es } from 'date-fns/locale';

interface CustomTooltipProps {
  seriesIndex: number;
  dataPointIndex: number;
  w: any;
  selectedCurrency: string;
  chartType: 'variety' | 'boxFormat' | 'caliber' | 'market' | 'color' | 'fleet';
  priceView?: 'kg' | 'box';
}

const CustomTooltip: React.FC<CustomTooltipProps> = ({
  seriesIndex,
  dataPointIndex,
  w,
  selectedCurrency,
  chartType,
  priceView = 'kg',
}) => {
  // Obtenemos el punto actual
  const currentSeries = w.globals.initialSeries[seriesIndex];
  const currentPoint = currentSeries.data[dataPointIndex];

  if (!currentPoint) return '';

  // Parse date string to avoid timezone issues
  let date: string;
  const xValue = currentPoint.x;

  if (!xValue) {
    // Handle null/undefined
    date = '';
  } else if (typeof xValue === 'string' && xValue.match(/^\d{4}-\d{2}-\d{2}$/)) {
    // Parse YYYY-MM-DD as local date to avoid timezone issues
    const [year, month, day] = xValue.split('-').map(Number);
    const localDate = new Date(year, month - 1, day);
    date = format(localDate, 'dd MMM', { locale: es });
  } else if (typeof xValue === 'number' && xValue > 1000000) {
    // Handle timestamp (legacy support)
    date = format(new Date(xValue), 'dd MMM', { locale: es });
  } else if (typeof xValue === 'number') {
    // If it's a small number, it might be an index
    const label = w?.globals?.labels?.[xValue];
    if (label && typeof label === 'string' && label.match(/^\d{4}-\d{2}-\d{2}$/)) {
      const [year, month, day] = label.split('-').map(Number);
      const localDate = new Date(year, month - 1, day);
      date = format(localDate, 'dd MMM', { locale: es });
    } else {
      date = label ? String(label) : String(xValue);
    }
  } else if (typeof xValue === 'string' && xValue.startsWith('W')) {
    // Handle Week format (e.g., "W45")
    const weekNum = xValue.substring(1);
    date = `Semana ${weekNum}`;
  } else {
    // Fallback - use the value as is
    date = String(xValue);
  }

  let content = `<div class="apexcharts-tooltip-title">${date}</div>`;

  // Usar la misma lógica que AccumulatedFobChart
  const allSeriesData = w.globals.initialSeries
    .map((series) => {
      const point = series.data.find((p) => p.x === currentPoint.x);
      return point
        ? {
          name: series.name,
          value: point.y,
          color: series.color,
        }
        : null;
    })
    .filter((item) => item !== null)
    .sort((a, b) => b.value - a.value);

  allSeriesData.forEach(({ name, value, color }, index) => {
    content += `
      <div class="apexcharts-tooltip-series-group" style="order: ${index}; display: flex !important;">
        <span class="apexcharts-tooltip-marker" style="background-color: ${color};"></span>
        <div class="apexcharts-tooltip-text">
          <div class="apexcharts-tooltip-y-group">
            <span class="apexcharts-tooltip-text-y-label">${name}: </span>
            <span class="apexcharts-tooltip-text-y-value">
              ${typeof value === 'number' ? value.toFixed(2) : 'N/A'
      } ${selectedCurrency}/${priceView}
            </span>
          </div>
        </div>
      </div>
    `;
  });

  return content;
};

export default CustomTooltip;
